/* eslint-disable no-console */
'use client';

import type {
  AttributesEntity,
  VariantsEntity,
} from '@eliadmin/core/src/api/products/types/product-types';
import {
  sortVariantsByAvailability,
  useVariantsStore,
} from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useMemo } from 'react';

function buildVariantLabel(variant: VariantsEntity) {
  // Prefer server-provided name if present
  const base = (variant.name ?? '').trim();
  if (base) return base;

  // Fallback: join value names
  const parts = (variant.values ?? [])
    .filter(Boolean)
    .sort((a, b) => (a.attribute_position ?? 0) - (b.attribute_position ?? 0))
    .map(v => v.name)
    .filter(Boolean);

  return parts.length ? parts.join(' / ') : `Variant #${variant.id}`;
}

function isVariantSelectable(variant: VariantsEntity) {
  // Choose your rule:
  // - allow if quantity > 0 OR availability.sellable
  // - block if neither is true
  return variant.quantity > 0 || !!variant.availability?.sellable;
}

export function ProductVariants() {
  const attributes = useVariantsStore(s => s.product.attributes) || [];
  const variants = useVariantsStore(s => s.product.variants) || [];

  const selectedVariant = useVariantsStore(s => s.selectedVariant);
  const setSelectedVariant = useVariantsStore(s => s.setSelectedVariant);

  // Keep your existing "select first available on mount" behavior,
  // but make it variant-based (no manual selectedValues mutation needed).
  useEffect(() => {
    try {
      if (selectedVariant) return;
      if (!Array.isArray(variants) || variants.length === 0) return;

      const sorted = sortVariantsByAvailability(variants) || [];
      const first = sorted.find(v => v && isVariantSelectable(v)) ?? sorted[0];
      if (!first) return;

      setSelectedVariant(first);
    } catch (e) {
      console.warn('Failed to initialize default variant', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedVariants = useMemo(() => {
    const sorted = sortVariantsByAvailability(variants) || [];
    return sorted;
  }, [variants]);

  if (!attributes.length || !variants.length) return null;

  if (sortedVariants.length > 1)
    return (
      <div className={cn('flex flex-col gap-4', 'ea-product-detail-variants')}>
        <div className={cn('flex flex-col gap-2', 'ea-product-detail-variants__variants')}>
          <p className="text-sm font-semibold text-foreground">Varianta produktu</p>
          <div className="flex flex-wrap items-start justify-start gap-2">
            {sortedVariants.map(variant => {
              const active = selectedVariant?.id === variant.id;
              const selectable = isVariantSelectable(variant);

              return (
                <button
                  key={`variant-${variant.id}`}
                  type="button"
                  onClick={() => setSelectedVariant(variant)}
                  disabled={!selectable}
                  className={cn(
                    'h-[28px] w-fit whitespace-nowrap rounded-[6px] border px-3 text-xs font-normal transition-all sm:h-[34px] sm:text-sm',
                    active
                      ? 'cursor-context-menu border-foreground bg-foreground text-white'
                      : 'border-border bg-transparent hover:bg-border',
                    selectable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
                  )}
                >
                  {buildVariantLabel(variant)}
                </button>
              );
            })}
          </div>
          <p className="text-sm font-light text-[#666464]">Varianta: {selectedVariant?.code}</p>
        </div>

        {/* Optional: keep the attribute UI below as read-only/visual confirmation,
          or keep it interactive if you still want users to adjust after selecting a variant. */}
        {/* If you want to remove it entirely, delete everything below. */}
      </div>
    );
}
